import React, { ReactNode, useMemo } from 'react'
import { Steps, Tooltip } from 'antd'
import { CheckOutlined, CloseOutlined } from '@ant-design/icons'
import { classNames } from '../../../lib/classNames/classNames'
import cls from '../Steps/Steps.module.css'
import './index.css'

type Status = 'wait' | 'process' | 'finish' | 'error'
type Direction = 'horizontal' | 'vertical'

export interface StepItem {
  description?: {
    text: string
    color: string
  }
  disabled?: boolean
  icon?: ReactNode
  status?: Status
  subTitle?: ReactNode
  name: string
  code: string
}

interface HistoryItem {
  code?: string
  date_start?: string
  date_end?: string
  percent?: number
  description?: string
}

export interface StepsProps {
  className?: string
  current: number
  direction: Direction
  items: StepItem[]
  history?: HistoryItem[]
}

export const StepsUi: React.FC<StepsProps> = (props: StepsProps) => {
  const {
    direction,
    current,
    className,
    items,
    history = [],
    ...otherProps
  } = props
  const { Step } = Steps
  const optionsList = useMemo(() => {
    const historySteps = history.map(historyItem => historyItem.code)
    return items.map((optionItem, index) => {
      let status: Status = 'wait'
      if (index < current) {
        status = 'finish'
      } else if (index === current) {
        status = 'process'
      }

      if (!historySteps.includes(optionItem.code) && index < current && !optionItem.icon) {
        status = 'error'
      }

      let customIcon: ReactNode | undefined
      if (status !== 'process') {
        if (optionItem.icon) {
          customIcon = optionItem.icon
        } else if (status === 'finish') {
          customIcon = (
            <div className="cardStepIcon cardStepIcon--finish">
              <CheckOutlined />
            </div>
          )
        } else if (status === 'error') {
          customIcon = (
            <div className="cardStepIcon cardStepIcon--error">
              <CloseOutlined />
            </div>
          )
        } else {
          customIcon = (
            <div className="cardStepIcon cardStepIcon--wait">{index + 1}</div>
          )
        }
      }

      const iconWithTooltip = status !== 'process' && customIcon
        ? (
          <Tooltip title={optionItem.name} placement="top">
            {customIcon}
          </Tooltip>
        )
        : undefined

      return (
        <Step
          className="cardStep"
          title={optionItem.name}
          key={optionItem.code}
          status={status}
          icon={iconWithTooltip}
          description={
            optionItem.description ? (
              <span style={{ color: optionItem.description?.color }}>
                {optionItem.description?.text}
              </span>
            ) : null
          }
        />
      )
    })
  }, [items, history, current])

  return <Steps
    className={classNames(cls.Step, {}, [className])}
    direction={direction}
    current={current}
    size="small"
    percent={history[history.length - 1] && history[history.length - 1].percent || undefined}
    {...otherProps}
  >
    {optionsList}
  </Steps>
}
